import {
  getDoc,
  doc,
  deleteDoc,
  collection,
  getDocs,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  query,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { ExerciseSet, WOD } from "../pages/company/types";
import { firestoreDb } from "../App";
import { getFormattedDate } from "../utils/commons";

const WOD_PAGE_SIZE = 20;

/** Single doc holding the exercise library as `{ exercises: string[] }`. */
const EXERCISES_COLLECTION = "exercises";
const EXERCISE_LIBRARY_DOC = "library";

const getExerciseLibraryRef = () =>
  doc(firestoreDb, EXERCISES_COLLECTION, EXERCISE_LIBRARY_DOC);

export const fetchExercises = (): Promise<
  Array<{ name: string; id: string }>
> => {
  const docRef = getExerciseLibraryRef();
  return getDoc(docRef)
    .then((docSnap) => {
      if (docSnap.data()) {
        let exerciseList = docSnap.data()?.exercises || [];
        exerciseList = exerciseList.map((_exercise: string) => {
          return {
            name: _exercise,
            id: _exercise.toLowerCase(),
          };
        });
        return Promise.resolve(exerciseList);
      }
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};

export const getTodaysWod = (): Promise<Array<ExerciseSet>> => {
  const formattedDate = getFormattedDate();
  const docRef = doc(firestoreDb, "wods", formattedDate);
  return getDoc(docRef)
    .then((docSnap) => {
      if (docSnap.data()) {
        return Promise.resolve(docSnap.data()?.sets || []);
      } else {
        return Promise.resolve([]);
      }
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};

export const deleteWod = (date?: string) => {
  let dateToDelete = getFormattedDate();
  if (date) {
    dateToDelete = date;
  }

  return deleteDoc(doc(firestoreDb, "wods", dateToDelete))
    .then((deleted) => {
      return Promise.resolve();
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};

export const fetchPastWods = (
  lastDoc?: QueryDocumentSnapshot<DocumentData>
): Promise<{
  wods: WOD[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  hasMore: boolean;
}> => {
  let q = query(
    collection(firestoreDb, "wods"),
    orderBy("createdAt", "desc"),
    limit(WOD_PAGE_SIZE)
  );
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }
  return getDocs(q)
    .then((snap) => {
      const lastVisible = snap.docs[snap.docs.length - 1];
      return {
        wods: snap.docs.map((d) => d.data() as WOD),
        lastDoc: lastVisible,
        hasMore: snap.docs.length === WOD_PAGE_SIZE,
      };
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};

export const saveWod = (
  date: string,
  sets: ExerciseSet[],
  duration: number
): Promise<boolean> => {
  return setDoc(doc(firestoreDb, "wods", date), {
    date,
    sets,
    duration,
    createdAt: Date.now(),
  })
    .then(() => Promise.resolve(true))
    .catch((e) => Promise.reject(e));
};

export const cloneWorkout = (wod: WOD): Promise<boolean> => {
  const formattedDate = getFormattedDate();
  return setDoc(doc(firestoreDb, "wods", formattedDate), {
    date: formattedDate,
    sets: wod.sets,
    duration: wod.duration,
    createdAt: Date.now(),
  })
    .then(() => {
      return Promise.resolve(true);
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};

export const saveNewExercise = (exercise: string): Promise<boolean> => {
  const exerciseListRef = getExerciseLibraryRef();

  return updateDoc(exerciseListRef, {
    exercises: arrayUnion(exercise),
  })
    .then(() => {
      return Promise.resolve(true);
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};

export const deleteExercise = (exercise: string): Promise<boolean> => {
  const exerciseListRef = getExerciseLibraryRef();

  return updateDoc(exerciseListRef, {
    exercises: arrayRemove(exercise),
  })
    .then(() => {
      return Promise.resolve(true);
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};
